import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, FormFeedback, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner, Table } from "reactstrap";
import Select from "react-select";
import { FaBan, FaPen, FaPlus, FaRedo } from "react-icons/fa";

import api from "services/api";
import { useToast } from "hooks/toast";
import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import ShellCard, { TableSurface } from "components/ShellCard";
import TablePagination from "components/TablePagination";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { exportCsv } from "utils/exportCsv";
import { reactSelectPortalProps, reactSelectStyles } from "utils/reactSelectStyles";
import {
  ConfirmModal,
  ListState,
  STATUS_ALL,
  STATUS_OPTIONS,
  StatusPill,
  TEXTO_SOMENTE_CONSULTA,
  mutedStyle,
  toastError,
  toastSuccess,
} from "../shared";

// Cadastros Gerais › CEP (sprint 638). Antes a rota só mostrava um quadro de
// referência do TMS; o CRUD já existia no backend_hub (/endereco/ceps, com
// paginação e filtros no servidor) e agora tem tela. Inativar = DELETE (soft:
// Status 0); reativar = PUT com status 1. O CEP em si não muda na edição.

const URL_BASE = "/endereco/ceps";
const UFS = [
  "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO",
  "RR", "RS", "SC", "SE", "SP", "TO",
];
const UF_OPTIONS = [{ value: "", label: "Todas" }].concat(UFS.map((uf) => ({ value: uf, label: uf })));

function soDigitos(value) {
  return String(value || "").replace(/\D/g, "");
}

export function formatCep(value) {
  const d = soDigitos(value).padStart(8, "0").slice(-8);
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

function emptyFilters() {
  return { busca: "", uf: "", cidade: "", bairro: "", idTipoLogradouro: "", status: STATUS_ALL };
}

function emptyForm() {
  return {
    cep: "",
    idTipoLogradouro: "",
    logradouro: "",
    complemento: "",
    local: "",
    bairro: "",
    uf: "",
    idCidade: "",
    cidade: "",
    status: "1",
  };
}

// Busca vazia, só dígitos (CEP inteiro ou começo) ou texto (logradouro).
export function paramsDaBusca(filters, page, pageSize) {
  const params = { page, pageSize };
  const busca = String(filters.busca || "").trim();
  if (busca) {
    const semMascara = busca.replace(/[.\-\s]/g, "");
    if (/^\d+$/.test(semMascara)) params.cep = semMascara;
    else params.logradouro = busca;
  }
  if (filters.uf) params.uf = filters.uf;
  if (filters.cidade.trim()) params.cidade = filters.cidade.trim();
  if (filters.bairro.trim()) params.bairro = filters.bairro.trim();
  if (filters.idTipoLogradouro) params.idTipoLogradouro = filters.idTipoLogradouro;
  if (filters.status === "active") params.status = "1";
  if (filters.status === "inactive") params.status = "0";
  return params;
}

function logradouroCompleto(row) {
  return [row.TipoLogradouro, row.Logradouro].filter(Boolean).join(" ");
}

// somenteLeitura: nos produtos do cliente (consulta); a edição fica no Everlog
// Admin › Cadastros globais (sprint 638).
const Cep = ({ somenteLeitura = false }) => {
  const { addToast } = useToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [tiposLogradouro, setTiposLogradouro] = useState([]);

  const [isFormOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [consultandoCep, setConsultandoCep] = useState(false);
  const [cidadeSugestoes, setCidadeSugestoes] = useState([]);
  const cidadeTimer = useRef(null);

  const [toInativar, setToInativar] = useState(null);
  const [inativando, setInativando] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const result = await api.get(URL_BASE, { params: paramsDaBusca(applied, page, pageSize) });
      const data = result.data || {};
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(Number(data.total) || 0);
    } catch (error) {
      setRows([]);
      setTotal(0);
      setLoadError("Não foi possível buscar os CEPs.");
      toastError(addToast, "Erro ao carregar", error, "Não foi possível buscar os CEPs.");
    } finally {
      setLoading(false);
    }
  }, [addToast, applied, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let mounted = true;
    api
      .get("/endereco/tipos-logradouro")
      .then((res) => {
        if (mounted) setTiposLogradouro((Array.isArray(res.data) ? res.data : []).map((t) => ({ value: String(t.Id), label: t.Nome })));
      })
      .catch(() => {
        if (mounted) setTiposLogradouro([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(
    () => () => {
      if (cidadeTimer.current) clearTimeout(cidadeTimer.current);
    },
    []
  );

  const aplicar = (next) => {
    setApplied(next || draft);
    setPage(1);
  };

  const limpar = () => {
    const vazio = emptyFilters();
    setDraft(vazio);
    aplicar(vazio);
  };

  const updateDraft = (field, value) => setDraft((current) => ({ ...current, [field]: value }));

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormErrors({});
    setCidadeSugestoes([]);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      cep: formatCep(row.CEP),
      idTipoLogradouro: row.IdTipoLogradouro ? String(row.IdTipoLogradouro) : "",
      logradouro: row.Logradouro || "",
      complemento: row.Complemento || "",
      local: row.Local || "",
      bairro: row.Bairro || "",
      uf: row.UF || "",
      idCidade: row.IdCidade ? String(row.IdCidade) : "",
      cidade: row.Cidade || "",
      status: row.Status === 0 || row.Status === false ? "0" : "1",
    });
    setFormErrors({});
    setCidadeSugestoes([]);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (!saving) setFormOpen(false);
  };

  const updateForm = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      // Trocar a UF invalida a cidade escolhida.
      if (field === "uf" && value !== current.uf) {
        next.idCidade = "";
        next.cidade = "";
      }
      return next;
    });
    setFormErrors((current) => {
      if (!current[field] && !(field === "uf" && current.idCidade)) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  // CEP com 8 dígitos (cadastro novo): busca logradouro, bairro e cidade para
  // preencher; nada é sobrescrito se o usuário já digitou.
  const consultarCep = async () => {
    if (editing) return;
    const cep = soDigitos(form.cep);
    if (cep.length !== 8) return;
    setConsultandoCep(true);
    try {
      const { data } = await api.get(`/endereco/cep/${cep}/preview`);
      setForm((f) => ({
        ...f,
        logradouro: f.logradouro || data.logradouro || "",
        bairro: f.bairro || data.bairro || "",
        uf: f.uf || data.uf || "",
        cidade: f.idCidade ? f.cidade : data.cidade || f.cidade,
        idCidade: f.idCidade || (data.idCidade ? String(data.idCidade) : ""),
      }));
    } catch (_) {
      // Pré-preenchimento é só ajuda: sem resposta, o usuário preenche à mão.
    } finally {
      setConsultandoCep(false);
    }
  };

  const buscarCidades = (texto) => {
    setForm((f) => ({ ...f, cidade: texto, idCidade: "" }));
    if (cidadeTimer.current) clearTimeout(cidadeTimer.current);
    if (!form.uf || texto.trim().length < 2) {
      setCidadeSugestoes([]);
      return;
    }
    cidadeTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/cidades", { params: { uf: form.uf, q: texto.trim(), limit: 15 } });
        setCidadeSugestoes(Array.isArray(data) ? data : []);
      } catch (_) {
        setCidadeSugestoes([]);
      }
    }, 250);
  };

  const escolherCidade = (cidade) => {
    setForm((f) => ({ ...f, cidade: cidade.nome, idCidade: String(cidade.id) }));
    setCidadeSugestoes([]);
    setFormErrors((current) => {
      const next = { ...current };
      delete next.idCidade;
      return next;
    });
  };

  const validar = () => {
    const errors = {};
    if (!editing && soDigitos(form.cep).length !== 8) errors.cep = "Informe o CEP com 8 dígitos.";
    if (!form.uf) errors.uf = "Escolha a UF.";
    if (!form.idCidade) errors.idCidade = "Escolha a cidade na lista.";
    return errors;
  };

  const payloadDo = (f) => ({
    cep: soDigitos(f.cep),
    idTipoLogradouro: f.idTipoLogradouro ? Number(f.idTipoLogradouro) : null,
    logradouro: f.logradouro.trim() || null,
    complemento: f.complemento.trim() || null,
    local: f.local.trim() || null,
    bairro: f.bairro.trim() || null,
    idCidade: Number(f.idCidade),
    status: f.status === "1" ? 1 : 0,
  });

  const salvar = async () => {
    const errors = validar();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    try {
      setSaving(true);
      if (editing) await api.put(`${URL_BASE}/${editing.Id}`, payloadDo(form));
      else await api.post(URL_BASE, payloadDo(form));
      toastSuccess(addToast, editing ? "CEP atualizado" : "CEP cadastrado", `${formatCep(form.cep)} foi salvo.`);
      setFormOpen(false);
      load();
    } catch (error) {
      toastError(addToast, "Erro ao salvar", error, "Não foi possível salvar o CEP.");
    } finally {
      setSaving(false);
    }
  };

  const inativar = async () => {
    if (!toInativar) return;
    try {
      setInativando(true);
      await api.delete(`${URL_BASE}/${toInativar.Id}`);
      toastSuccess(addToast, "CEP inativado", `${formatCep(toInativar.CEP)} não aparece mais como ativo.`);
      setToInativar(null);
      load();
    } catch (error) {
      toastError(addToast, "Não foi possível inativar", error, "Erro ao inativar o CEP.");
      setToInativar(null);
    } finally {
      setInativando(false);
    }
  };

  const reativar = async (row) => {
    try {
      await api.put(`${URL_BASE}/${row.Id}`, {
        idTipoLogradouro: row.IdTipoLogradouro || null,
        logradouro: row.Logradouro || null,
        complemento: row.Complemento || null,
        local: row.Local || null,
        idBairro: row.IdBairro || null,
        idCidade: row.IdCidade,
        status: 1,
      });
      toastSuccess(addToast, "CEP reativado", `${formatCep(row.CEP)} voltou a ficar ativo.`);
      load();
    } catch (error) {
      toastError(addToast, "Não foi possível reativar", error, "Erro ao reativar o CEP.");
    }
  };

  const exportar = () =>
    exportCsv({
      filename: "ceps_pagina",
      columns: [
        { label: "CEP", getValue: (row) => formatCep(row.CEP) },
        { label: "Tipo de logradouro", key: "TipoLogradouro" },
        { label: "Logradouro", key: "Logradouro" },
        { label: "Complemento", key: "Complemento" },
        { label: "Bairro", key: "Bairro" },
        { label: "Local", key: "Local" },
        { label: "Cidade", key: "Cidade" },
        { label: "UF", key: "UF" },
        { label: "Status", getValue: (row) => (row.Status === 0 || row.Status === false ? "Inativo" : "Ativo") },
      ],
      rows,
    });

  const activeFiltersCount = [applied.uf, applied.cidade.trim(), applied.bairro.trim(), applied.idTipoLogradouro, applied.status !== STATUS_ALL]
    .filter(Boolean).length;
  const hasFilters = Boolean(String(applied.busca).trim()) || activeFiltersCount > 0;
  const tipoOptions = [{ value: "", label: "Todos" }].concat(tiposLogradouro);
  const showState = loading || loadError || rows.length === 0;

  return (
    <div className="content cep-page">
      <Modal isOpen={isFormOpen} toggle={closeForm} modalClassName="modal-align-top" fade={false} size="lg">
        <ModalHeader toggle={closeForm} tag="h5">
          {editing ? `Editar CEP ${formatCep(editing.CEP)}` : "Novo CEP"}
        </ModalHeader>
        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          <div className="cg-modal-subtitle">
            {editing
              ? "O número do CEP não muda; ajuste endereço, cidade e status."
              : "Digite o CEP: logradouro, bairro e cidade são preenchidos quando o CEP é encontrado."}
          </div>
          <Row>
            <Col md="3">
              <FormGroup>
                <Label for="cep-numero">CEP</Label>
                <Input
                  id="cep-numero"
                  value={form.cep}
                  disabled={Boolean(editing)}
                  maxLength={9}
                  placeholder="00000-000"
                  invalid={Boolean(formErrors.cep)}
                  onChange={(e) => updateForm("cep", e.target.value)}
                  onBlur={consultarCep}
                />
                {formErrors.cep ? <FormFeedback>{formErrors.cep}</FormFeedback> : null}
                {consultandoCep ? (
                  <small style={mutedStyle}>
                    <Spinner size="sm" /> Consultando CEP...
                  </small>
                ) : null}
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label for="cep-tipo-logradouro">Tipo</Label>
                <Input id="cep-tipo-logradouro" type="select" value={form.idTipoLogradouro} onChange={(e) => updateForm("idTipoLogradouro", e.target.value)}>
                  <option value="">—</option>
                  {tiposLogradouro.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="cep-logradouro">Logradouro</Label>
                <Input id="cep-logradouro" value={form.logradouro} onChange={(e) => updateForm("logradouro", e.target.value)} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="cep-complemento">Complemento</Label>
                <Input id="cep-complemento" value={form.complemento} onChange={(e) => updateForm("complemento", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="cep-local">Local</Label>
                <Input id="cep-local" value={form.local} onChange={(e) => updateForm("local", e.target.value)} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="cep-bairro">Bairro</Label>
                <Input id="cep-bairro" value={form.bairro} onChange={(e) => updateForm("bairro", e.target.value)} />
                <small style={mutedStyle}>Bairro novo é criado na cidade escolhida.</small>
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label for="cep-uf">UF</Label>
                <Input id="cep-uf" type="select" value={form.uf} invalid={Boolean(formErrors.uf)} onChange={(e) => updateForm("uf", e.target.value)}>
                  <option value="">—</option>
                  {UFS.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </Input>
                {formErrors.uf ? <FormFeedback>{formErrors.uf}</FormFeedback> : null}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup style={{ position: "relative" }}>
                <Label for="cep-cidade">Cidade</Label>
                <Input
                  id="cep-cidade"
                  value={form.cidade}
                  disabled={!form.uf}
                  autoComplete="off"
                  placeholder={form.uf ? "Digite 2 letras e escolha na lista" : "Escolha a UF antes"}
                  invalid={Boolean(formErrors.idCidade)}
                  onChange={(e) => buscarCidades(e.target.value)}
                />
                {formErrors.idCidade ? <FormFeedback>{formErrors.idCidade}</FormFeedback> : null}
                {cidadeSugestoes.length ? (
                  <div className="cg-cidade-sugestoes" data-testid="cidade-sugestoes">
                    {cidadeSugestoes.map((c) => (
                      <button type="button" key={c.id} onClick={() => escolherCidade(c)}>
                        {c.nome}
                      </button>
                    ))}
                  </div>
                ) : null}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="cep-status">Status</Label>
                <Input id="cep-status" type="select" value={form.status} onChange={(e) => updateForm("status", e.target.value)}>
                  <option value="1">Ativo</option>
                  <option value="0">Inativo</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <div className="cg-modal-footer">
            <Button color="default" outline size="sm" onClick={closeForm} disabled={saving}>
              Cancelar
            </Button>
            <Button color="info" size="sm" onClick={salvar} disabled={saving || consultandoCep} data-testid="salvar">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <ConfirmModal
        isOpen={Boolean(toInativar)}
        title="Inativar CEP"
        confirmLabel="Inativar"
        busyLabel="Inativando..."
        busy={inativando}
        onConfirm={inativar}
        onCancel={() => setToInativar(null)}
      >
        Inativar o CEP <strong>{toInativar ? formatCep(toInativar.CEP) : ""}</strong>
        {toInativar && toInativar.Cidade ? ` (${toInativar.Cidade}/${toInativar.UF})` : ""}? Ele continua no cadastro e pode ser
        reativado.
      </ConfirmModal>

      <PageHero
        eyebrow={somenteLeitura ? "Cadastros gerais" : "Cadastros globais"}
        title="CEP"
        description={
          somenteLeitura
            ? `Base de endereçamento do sistema: cada CEP com seu logradouro, bairro e cidade. Busque por CEP, logradouro, cidade ou bairro. ${TEXTO_SOMENTE_CONSULTA}`
            : "Base de endereçamento do sistema: cada CEP com seu logradouro, bairro e cidade. Busque por CEP, logradouro, cidade ou bairro; cadastre o que faltar."
        }
        actions={
          <>
            <PageHeroRefreshButton onClick={load} loading={loading} />
            <PageHeroExportButton onClick={exportar} disabled={loading || rows.length === 0} label="Exportar página" />
            {somenteLeitura ? null : (
              <Button color="info" className="btn-sm" onClick={openCreate}>
                <FaPlus style={{ marginRight: 6 }} />
                Novo CEP
              </Button>
            )}
          </>
        }
      />

      <FilterBand
        mode="server"
        searchLabel="Buscar CEP"
        searchHint="CEP (inteiro ou o começo) ou nome do logradouro"
        searchPlaceholder="Ex.: 01310-100, 0131 ou Paulista"
        searchValue={draft.busca}
        onSearchChange={(value) => updateDraft("busca", value)}
        onSearchSubmit={(value) => aplicar({ ...draft, busca: value })}
        activeFiltersCount={activeFiltersCount}
        panelTitle="Filtros"
        panelDescription="Refine por UF, cidade, bairro, tipo de logradouro e status. A busca roda no servidor."
        onApply={() => aplicar()}
        onClear={limpar}
        busy={loading}
      >
        <Row>
          <Col md="2">
            <Label for="cep-filtro-uf" style={{ color: AUTOPILOT_COLORS.muted }}>
              UF
            </Label>
            <Select
              inputId="cep-filtro-uf"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={UF_OPTIONS}
              value={UF_OPTIONS.find((o) => o.value === draft.uf) || UF_OPTIONS[0]}
              onChange={(opt) => updateDraft("uf", opt ? opt.value : "")}
            />
          </Col>
          <Col md="3">
            <Label for="cep-filtro-cidade" style={{ color: AUTOPILOT_COLORS.muted }}>
              Cidade
            </Label>
            <Input id="cep-filtro-cidade" value={draft.cidade} placeholder="Nome contém" onChange={(e) => updateDraft("cidade", e.target.value)} />
          </Col>
          <Col md="3">
            <Label for="cep-filtro-bairro" style={{ color: AUTOPILOT_COLORS.muted }}>
              Bairro
            </Label>
            <Input id="cep-filtro-bairro" value={draft.bairro} placeholder="Nome contém" onChange={(e) => updateDraft("bairro", e.target.value)} />
          </Col>
          <Col md="2">
            <Label for="cep-filtro-tipo" style={{ color: AUTOPILOT_COLORS.muted }}>
              Tipo de logradouro
            </Label>
            <Select
              inputId="cep-filtro-tipo"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={tipoOptions}
              value={tipoOptions.find((o) => o.value === draft.idTipoLogradouro) || tipoOptions[0]}
              onChange={(opt) => updateDraft("idTipoLogradouro", opt ? opt.value : "")}
            />
          </Col>
          <Col md="2">
            <Label for="cep-filtro-status" style={{ color: AUTOPILOT_COLORS.muted }}>
              Status
            </Label>
            <Select
              inputId="cep-filtro-status"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find((o) => o.value === draft.status) || STATUS_OPTIONS[0]}
              onChange={(opt) => updateDraft("status", opt ? opt.value : STATUS_ALL)}
              isSearchable={false}
            />
          </Col>
        </Row>
      </FilterBand>

      <ShellCard title="CEPs encontrados" description={`${total.toLocaleString("pt-BR")} resultado(s)`}>
        {showState ? (
          <ListState
            loading={loading}
            error={loadError}
            empty={rows.length === 0}
            loadingText="Buscando CEPs…"
            emptyTitle="Nenhum CEP encontrado"
            emptyText={
              somenteLeitura
                ? hasFilters
                  ? `Ajuste a busca ou os filtros. ${TEXTO_SOMENTE_CONSULTA}`
                  : TEXTO_SOMENTE_CONSULTA
                : hasFilters
                ? "Ajuste a busca ou os filtros. CEP que não existe pode ser cadastrado pelo botão acima."
                : "Cadastre o primeiro CEP pelo botão acima."
            }
            onRetry={load}
          />
        ) : (
          <TableSurface>
            <Table size="sm" responsive hover className="cg-table hub-thead-accent" style={{ minWidth: 960 }} data-testid="cep-tabela">
              <colgroup>
                <col style={{ width: 110 }} />
                <col />
                <col style={{ width: "17%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: 96 }} />
                {somenteLeitura ? null : <col style={{ width: 92 }} />}
              </colgroup>
              <thead className="text-primary">
                <tr>
                  <th>CEP</th>
                  <th>Logradouro</th>
                  <th>Bairro</th>
                  <th>Cidade / UF</th>
                  <th>Local</th>
                  <th>Status</th>
                  {somenteLeitura ? null : <th className="text-right">Ações</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const ativo = !(row.Status === 0 || row.Status === false);
                  return (
                    <tr key={row.Id}>
                      <td className="cg-mono">{formatCep(row.CEP)}</td>
                      <td>
                        {logradouroCompleto(row) || <span style={mutedStyle}>—</span>}
                        {row.Complemento ? <span className="cg-sub">{row.Complemento}</span> : null}
                      </td>
                      <td style={row.Bairro ? undefined : mutedStyle}>{row.Bairro || "—"}</td>
                      <td>
                        {row.Cidade} <span style={mutedStyle}>/ {row.UF}</span>
                      </td>
                      <td style={row.Local ? undefined : mutedStyle}>{row.Local || "—"}</td>
                      <td>
                        <StatusPill active={ativo} />
                      </td>
                      {somenteLeitura ? null : (
                        <td className="text-right">
                          <span className="cg-acoes">
                            <Button color="link" size="sm" className="btn-icon" title="Editar" onClick={() => openEdit(row)}>
                              <FaPen />
                            </Button>
                            {ativo ? (
                              <Button color="link" size="sm" className="btn-icon text-danger" title="Inativar" onClick={() => setToInativar(row)}>
                                <FaBan />
                              </Button>
                            ) : (
                              <Button color="link" size="sm" className="btn-icon" title="Reativar" onClick={() => reativar(row)}>
                                <FaRedo />
                              </Button>
                            )}
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              pageSizes={[20, 50, 100]}
              itemLabel="CEP(s)"
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              disabled={loading}
            />
          </TableSurface>
        )}
      </ShellCard>
    </div>
  );
};

export default Cep;
